import { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

export interface RecaptchaData {
  token: string | null;
  error: string | null;
}

export default function useRecaptcha(placementName: string) {
  const RECAPTCHA_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isLoadingRecaptcha, setIsLoadingRecaptcha] = useState(false);
  const validPlacementName = placementName.match(/^[a-zA-Z0-9_]*$/);
  if (!validPlacementName) {
    console.warn(
      'Placement name should only contain alphanumeric characters and underscores',
      placementName,
    );
  }
  async function loadRecaptcha() {
    if (!validPlacementName) {
      console.warn('Recaptcha: placement name is invalid', placementName);
      return { token: null, error: 'invalid-placement-name' };
    } else if (!RECAPTCHA_KEY) {
      console.warn('Recaptcha: missing site key');
      return { token: null, error: 'missing-site-key' };
    } else if (!executeRecaptcha) {
      console.warn('Recaptcha: lib failed to load');
      return { token: null, error: 'lib-failed-to-load' };
    } else {
      try {
        setIsLoadingRecaptcha(true);
        console.info('Recaptcha: Loading token for placement', placementName);
        const token = await executeRecaptcha(
          `${process.env.NEXT_PUBLIC_PANEL}_${placementName}`.replace('-', '_'),
        );
        console.log('Recaptcha: Loaded token for placement', placementName, token);
        return { token, error: null };
      } catch (e: Error | any) {
        console.error('Recaptcha: Failed to load token', e);
        return { token: null, error: e.message };
      } finally {
        setIsLoadingRecaptcha(false);
      }
    }
  }
  return { loadRecaptcha, isLoadingRecaptcha };
}

export function RecaptchaProvider({ children }: { children: React.ReactNode }) {
  const RECAPTCHA_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

  return RECAPTCHA_KEY ? (
    <GoogleReCaptchaProvider
      useRecaptchaNet
      scriptProps={{
        async: true,
        defer: true,
      }}
      reCaptchaKey={RECAPTCHA_KEY}
    >
      {children}
    </GoogleReCaptchaProvider>
  ) : (
    <>{children}</>
  );
}
